import * as invert from 'lodash/invert'
import * as isNumber from 'lodash/isNumber'
import { PROVIDER_LOGO_MAP } from 'constants/providers'
import { CategoryId, MetaData } from 'interfaces/meta'
import {
  AccountKind,
  FullAccountKind,
  LiveUpdateStatus,
  ManualBalanceAccount,
  MixedCompositeId,
  MixedLinkageEntity,
  MixedLinkageEntityType,
  Provider,
  ProviderAccount,
  ProviderAccountState,
  ProviderBalanceAccount,
  LinkageSessionId,
  WithLinkageSessionId,
  WithMixedCompositeId,
  LinkageSession,
  ProviderAccountAction,
  ProviderAccountNotification,
  MixedBalanceAccount,
} from 'interfaces/linkage'
import { Action } from 'actions'
import { FLUSH_LIVE_UPDATE, performMixedAccountOperationActionCreator, POLL_LIVE_UPDATE } from 'actions/linkageActions'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { MixedAccountOperationApiResponse } from 'interfaces/ajax/payload'
import { State } from 'interfaces/state'
import { Observable, empty, from } from 'rxjs'
import { providerAccountSelector, createLinkageSessionByIdSelector } from 'selectors/linkageSelectors'
import { useMemo } from 'react'
import { useShallowSelector } from 'helpers/reactReduxHelpers'
import * as memoize from 'lodash/memoize'
import { Anchor } from 'interfaces/navigation'
import { SortProps } from 'components/features/pfm/LinkedAccounts/OrderBy'
import { getMemberInfoActionCreator } from 'actions/memberActions'

const SAVED_ORDER_STORE_KEY = 'linkedAccountsSortProps'
export const saveOrderState = (params: SortProps) => {
  try {
    localStorage.setItem(SAVED_ORDER_STORE_KEY, JSON.stringify(params))
  } catch (e) {}
}

export const loadOrderState = () => {
  try {
    const savedData = JSON.parse(localStorage.getItem(SAVED_ORDER_STORE_KEY) || 'null')
    if ('sortBy' in savedData && 'direction' in savedData) {
      return savedData
    }
  } catch (e) {}
  return null
}


export const getProviderAccountAnchorId = (cid: ProviderAccount['cid']): Anchor => ({
  anchor: 'linked-accounts_provider',
  id: cid,
})

export const accountActionsMap = memoize((actions: ProviderAccountState['actions']) => {

  return actions.reduce((acc, item) => ({
    ...acc,
    [item.id]: item,
  }), {} as Record<ProviderAccountAction['id'], ProviderAccountAction>)
})

export function getProviderLogoPath (name: string) {
  const commonLogo = PROVIDER_LOGO_MAP[name]
  return commonLogo ? `/images/provider-logos/${ commonLogo }.png` : null
}

type GetProviderInfoProps = {
  type: 'provider' | 'manual'
  account: MixedBalanceAccount | null,
  provider: Provider | null
  providerAccount: ProviderAccount | null
}
export const getProviderInfo = (props: GetProviderInfoProps) => {
  const { type, providerAccount, provider, account } = props
  if (type === 'manual' && !account) {
    return {
      providerName: 'Manual Entry',
      providerLogo: null,
      providerKey: 'unknown',
    }
  }
  if (type === 'manual' && account) {
    return {
      providerName: 'Manual Entry',
      providerLogo: null,
      providerKey: `manual ${ account.category }`,
    }
  }

  if (!providerAccount || !provider) {
    return {
      providerName: 'Manual Entry',
      providerLogo: null,
      providerKey: 'unknown',
    }
  }

  return {
    providerName: provider.name,
    providerLogo: getProviderLogoPath(provider.name) || provider.logo,
    providerKey: providerAccount.cid,
    providerUrl: provider.url,
  }
}

export function getAccountKind (category: CategoryId, meta: MetaData): AccountKind {
  const { debt_categories, asset_categories } = meta

  const isDebtAccount = debt_categories.allKeys.includes(category)
  const isAssetAccount = asset_categories.allKeys.includes(category)

  return !isDebtAccount && !isAssetAccount
    ? null // `null` means we don't know (it's a neither debt nor asset account)
    : (isDebtAccount ? 'debts' : 'assets')
}

export function getFullAccountKind (category: CategoryId, meta: MetaData): FullAccountKind {
  const { debt_categories, asset_categories, spending_categories, income_categories } = meta

  if (debt_categories.allKeys.includes(category)) {
    return 'debts'
  } else if (asset_categories.allKeys.includes(category)) {
    return 'assets'
  } else if (spending_categories.allKeys.includes(category)) {
    return 'spending'
  } else if (income_categories.allKeys.includes(category)) {
    return 'income'
  }

  return null
}


const compositePrefixMap: Record<MixedLinkageEntityType, string> = {
  provider: 'p',
  'provider-account': 'pa',
  'provider-balance-account': 'pba',
  'manual-balance-account': 'mba',
}
const invertedCompositePrefixMap: Record<string, MixedLinkageEntityType> = invert(compositePrefixMap)

export const createCompositeId = (type: MixedLinkageEntityType, id: number | null): MixedCompositeId =>
  `${ compositePrefixMap[type] }${ isNumber(id) ? `:${ id }` : '' }`

type LinkageEntityFromCid = {
  type: MixedLinkageEntityType
  id: number | null
}

const EXTRACT_LINKAGE_ENTITY_RE = /^(\w+)(?::(-?\d+))?$/
// TODO: test on fake provider account
export const extractLinkageEntityFromCid = (cid: MixedCompositeId): LinkageEntityFromCid | null => {
  const match = EXTRACT_LINKAGE_ENTITY_RE.exec(cid)
  if (!match) {
    return null
  }

  const entityType = invertedCompositePrefixMap[match[1]] || null
  if (!entityType) {
    return null
  }

  const id = match[2]
    ? parseInt(match[2], 10)
    : null

  return {
    type: entityType,
    id,
  }
}

const isEntityType = (type: MixedLinkageEntityType, mixedEntity: MixedLinkageEntity | null): boolean => {
  if (!mixedEntity) {
    return false
  }

  const entityFromCid = extractLinkageEntityFromCid(mixedEntity.cid)
  return !!entityFromCid && entityFromCid.type === type
}

export const isProvider = (mixedEntity: MixedLinkageEntity | null): mixedEntity is Provider =>
  isEntityType('provider', mixedEntity)
export const isProviderAccount = (mixedEntity: MixedLinkageEntity | null): mixedEntity is ProviderAccount =>
  isEntityType('provider-account', mixedEntity)
export const isProviderBalanceAccount = (mixedEntity: MixedLinkageEntity | null): mixedEntity is ProviderBalanceAccount =>
  isEntityType('provider-balance-account', mixedEntity)
export const isManualBalanceAccount = (mixedEntity: MixedLinkageEntity | null): mixedEntity is ManualBalanceAccount =>
  isEntityType('manual-balance-account', mixedEntity)

export const isFakeProviderAccountCid = (cid: MixedCompositeId) => {
  const split = cid.split(':')
  if (split.length !== 2) {
    return false
  }

  return parseInt(split[1], 10) < 0
}

export const isLiveUpdateWithStatus = (...status: LiveUpdateStatus[]) => (a: Action) => {
  if (a.type === POLL_LIVE_UPDATE && isSuccessAction(a)
    || a.type === FLUSH_LIVE_UPDATE && isSuccessAction(a)
  ) {
    return status.includes(a.payload.response.status)
  }

  return false
}

export const isInProgressState = (state: ProviderAccountState | null | undefined) =>
  !!state && ['verifying', 'gathering', 'retrieving'].includes(state.status)
export const isMFAState = (state: ProviderAccountState | null | undefined) =>
  !!state && state.status === 'mfa_required'


function isMixedAccountOperationApiResponse (response: any): response is MixedAccountOperationApiResponse {
  return Boolean(response && (response as MixedAccountOperationApiResponse).actions)
}

export const processApiActions = (response: MixedAccountOperationApiResponse | {}, state: State): Observable<Action> => {
  if (!isMixedAccountOperationApiResponse(response)) {
    return empty()
  }

  const processedApiActions = response.actions.reduce<Action[]>(
    (result, apiAction) => {
      if (apiAction.provider_account_id && apiAction.status === 'removed') {
        const cid = createCompositeId('provider-account', apiAction.provider_account_id)
        const providerAccount = providerAccountSelector(state, { cid })
        if (!providerAccount) {
          return result
        }

        const removeAction: Action = performMixedAccountOperationActionCreator.request({
          account: providerAccount,
          operation: 'delete',
          noop: true,
        })

        return result.concat(removeAction)
      }

      return result.concat(getMemberInfoActionCreator())
    },
    [],
  )

  return from(processedApiActions)
}

type UseCidResult = {
  withCid: WithMixedCompositeId<true>
  session: LinkageSession | null
}
export function useCidBySessionId (sessionId: LinkageSessionId): UseCidResult {
  const withSessionId: WithLinkageSessionId<true> = useMemo(() => ({
    sessionId,
  }), [sessionId])
  const sessionSelector = createLinkageSessionByIdSelector()
  const session = useShallowSelector((state) => sessionSelector(state, withSessionId))

  const cid = session ? session.cid : null
  const withCid = useMemo(() => ({
    cid,
  }), [cid])

  return { withCid, session }
}

export const shouldConfirmDeletion = (accountCid: MixedCompositeId, notificationType: ProviderAccountNotification) => {
  if (isFakeProviderAccountCid(accountCid)) {
    return false
  }

  return notificationType !== 'failure'
}
